/**
 * ScheduleReleaseStep2
 * Step 2: Review holidays and decide which ones to work
 *
 * @version 2025.1.0
 */
import React from 'react';
import {
  Box,
  Typography,
  Paper,
  Alert,
} from '@mui/material';
import { EventBusy, Info } from '@mui/icons-material';
import HolidayDecisionTable from './HolidayDecisionTable';
import {
  ScheduleReleasePreviewResponse,
  HolidayDecision,
  formatPeriod,
} from '../../../services/scheduleReleaseService';
import { WEEK_DAY_SHORT_NAMES, WeekDay } from '../../../types/professionalSchedule';

interface ScheduleReleaseStep2Props {
  preview: ScheduleReleasePreviewResponse;
  holidayDecisions: HolidayDecision[];
  onHolidayDecisionChange: (holidayId: number, workOnHoliday: boolean) => void;
}

const ScheduleReleaseStep2: React.FC<ScheduleReleaseStep2Props> = ({
  preview,
  holidayDecisions,
  onHolidayDecisionChange,
}) => {
  const workingDaysNames = preview.schedule.workingDays
    .map((day) => WEEK_DAY_SHORT_NAMES[day as WeekDay])
    .join(', ');

  const hasHolidays = holidayDecisions.length > 0;

  return (
    <Box>
      <Typography variant="h6" gutterBottom>
        Revisar Feriados
      </Typography>

      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Revise os feriados no período e decida se deseja trabalhar em cada um deles.
      </Typography>

      {/* Schedule Summary */}
      <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
          <Info color="primary" fontSize="small" />
          <Typography variant="subtitle2">Resumo da Configuração</Typography>
        </Box>

        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 2 }}>
          <Box>
            <Typography variant="caption" color="text.secondary">Profissional</Typography>
            <Typography variant="body2">{preview.schedule.professionalName}</Typography>
          </Box>
          <Box>
            <Typography variant="caption" color="text.secondary">Clínica</Typography>
            <Typography variant="body2">{preview.schedule.clinicName}</Typography>
          </Box>
          <Box>
            <Typography variant="caption" color="text.secondary">Período</Typography>
            <Typography variant="body2">{formatPeriod(preview.period.startDate, preview.period.endDate)}</Typography>
          </Box>
          <Box>
            <Typography variant="caption" color="text.secondary">Total de Dias</Typography>
            <Typography variant="body2">{preview.period.totalDays} dias</Typography>
          </Box>
          <Box>
            <Typography variant="caption" color="text.secondary">Dias de Trabalho</Typography>
            <Typography variant="body2">{workingDaysNames}</Typography>
          </Box>
          <Box>
            <Typography variant="caption" color="text.secondary">Duração do Slot</Typography>
            <Typography variant="body2">{preview.schedule.slotDuration} minutos</Typography>
          </Box>
        </Box>
      </Paper>

      {/* Holidays Section */}
      <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
          <EventBusy color="warning" fontSize="small" />
          <Typography variant="subtitle2">
            Feriados no Período ({holidayDecisions.length})
          </Typography>
        </Box>

        {hasHolidays ? (
          <HolidayDecisionTable
            decisions={holidayDecisions}
            onDecisionChange={onHolidayDecisionChange}
          />
        ) : (
          <Alert severity="success">
            Nenhum feriado encontrado no período selecionado.
          </Alert>
        )}
      </Paper>

      <Alert severity="info">
        <Typography variant="body2">
          <strong>Nota:</strong> Por padrao, nenhum slot sera gerado em feriados.
          Marque "Trabalhar" nos feriados em que deseja atender.
        </Typography>
      </Alert>
    </Box>
  );
};

export default ScheduleReleaseStep2;
